import React, { useCallback, useMemo, useEffect, useRef } from "react";
import { useFormContext } from "@/context/FormContext";
import { useLanguageContext } from "@/context/LanguageContext";
import { useGlobalStateContext } from "@/context/GlobalContext";
import { showToast } from "@int-traderjoes/ui-components-library";
import { getMandatoryFields } from "@/utils/helper";
import AuthService from "@/services/authService";
import petFoodNutritionPermissions from "@/utils/json/FieldPermissions/PetFoodNutrition.json";
import { transformSectionToNutritionForm } from "@/utils/nutritionSectionTransformer";
import { INutritionForm, NutritionTabItem } from "@/types/Nutrition.types";
import { PET_FOOD_DEFAULT_NUTRIENTS } from "@/constants/nutritionConstants";
import { isNutritionFactsNotRequiredFormat } from "@/utils/columnsToDeclareByFactsPanelFormat";
import { isProduceRacTjLabelProductSpecificationType } from "@/utils/productSpecificationTypeUtils";
import { isCompositeNumberAndUnitValid } from "@/utils/nutritionCompositeField";
import { isProteinClaimDvCalculationBasis } from "@/constants/nutritionDropdownConstants";
import { isPdcaasInValidRange } from "@/hooks/useNutrition";
import {
  getInvalidNutrientNames,
  normalizeNutrientName as normalizeNutrientNameShared,
} from "@/components/Nutrients/utils/validateNutrientRow";

export type NutritionDeclarationValidationResult = {
  isValid: boolean;
  hasNutrientErrors: boolean;
  invalidNutrientNames: string[];
};

// Constants - using name from API (not id)
export const DUAL_COLUMN_2016_FORMAT = '2016 Dual Column Nutrition Facts Label';
export const DUAL_WITH_SIDE_COLUMN_FORMAT = 'Dual Nutrition Facts Panel - with Sides';
export const DUAL_AS_PREPARED_FORMAT = 'Dual Nutrition Facts Panel - as Prepared';

// Helper function to check if format is dual column
const isDualColumnFormat = (format: string): boolean => {
  return format === DUAL_COLUMN_2016_FORMAT || format === DUAL_WITH_SIDE_COLUMN_FORMAT || format === DUAL_AS_PREPARED_FORMAT;
};

/**
 * Whether an errored form field now holds a valid value (same rules as validateSingleForm /
 * validateNutritionDataForDeclaration). `null` = can't tell from the form values (e.g. "nutrients",
 * which is validated from the nutrients table) -> leave the error alone.
 */
const isNutritionFieldFixed = (fieldName: string, form: any): boolean | null => {
  if (!form) return null;
  if (fieldName === "nutrients") return null;
  if (fieldName === "servingQuantityForCalculation") {
    return isCompositeNumberAndUnitValid(form.servingQuantityForCalculation);
  }
  const value = form[fieldName];
  const isEmpty =
    value === undefined ||
    value === null ||
    (typeof value === "string" && value.trim() === "");
  if (isEmpty) return false;
  if (fieldName === "pdcaas") return isPdcaasInValidRange(value);
  return true;
};

interface SectionValidationResult {
  sectionId: string;
  sectionName: string;
  isValid: boolean;
  errors: string[];
}

interface AllFormsValidationResult {
  allValid: boolean;
  results: SectionValidationResult[];
  invalidSections: SectionValidationResult[];
}

interface UseNutritionValidationReturn {
  validateNutritionForm: () => boolean;
  validateNutritionDataForDeclaration: () => NutritionDeclarationValidationResult;
  validateAllNutritionForms: () => AllFormsValidationResult;
  nutritionFormErrors: string[];
  setNutritionFormErrors: React.Dispatch<React.SetStateAction<string[]>>;
  clearValidationErrors: () => void;
  hasFieldError: (fieldName: string) => boolean;
  clearFieldError: (fieldName: string) => void;
  setFieldError: (fieldName: string) => void;
  isFieldConditionallyMandatory: (fieldName: string) => boolean;
}

export const useNutritionValidation = (): UseNutritionValidationReturn => {
  const { languageText } = useLanguageContext();
  const { nutritionForm, nutritionFormErrors, setNutritionFormErrors, nutritionSections, nutritionValidationErrors } = useFormContext();
  const { selectedActiveProject } = useGlobalStateContext();

  const errorTexts = languageText?.MESSAGES?.VALIDATION_MESSAGES;
  const isProduceRacTjLabel = useMemo(() => {
    const productSpecificationType = (selectedActiveProject?.productSpecificationType || selectedActiveProject?.category || '') as string;
    return isProduceRacTjLabelProductSpecificationType(productSpecificationType);
  }, [selectedActiveProject]);
  const isNutritionInfoNotRequired = useMemo(() => {
    if (!isProduceRacTjLabel) return false;
    const v = String((nutritionForm as any)?.nutritionInfoRequired ?? "Yes").trim().toLowerCase();
    return v === "no";
  }, [nutritionForm, isProduceRacTjLabel]);

  // Detect if this is a Pet Food specification
  const isPetFood = useMemo(() => {
    const productSpecificationType = (selectedActiveProject?.productSpecificationType || selectedActiveProject?.category || '') as string;
    return (productSpecificationType || '').toLowerCase().includes('pet');
  }, [selectedActiveProject]);
  const nutritionFormRef = useRef(nutritionForm);
  
  // Keep ref in sync with latest form state
  useEffect(() => {
    nutritionFormRef.current = nutritionForm;
  }, [nutritionForm]);
  
  // Get permissions for nutrition form - use Pet Food specific permissions if applicable
  const nutritionFormPermissions = useMemo(() => {
    if (isPetFood) {
      return petFoodNutritionPermissions.FIELD_PERMISSIONS;
    }
    return AuthService.fetchPermissions("Nutrition");
  }, [isPetFood]);

  // Helper function to check if a field is conditionally mandatory
  const isFieldConditionallyMandatory = useCallback((fieldName: string): boolean => {
    // Use ref to get latest form data to avoid stale closure issues
    const formToValidate = nutritionFormRef.current;
    
    // For Pet Food, dual column fields are never mandatory (they're hidden)
    if (isPetFood && (fieldName === 'column1Heading' || fieldName === 'column2Heading' || fieldName === 'amountPreparedText' || fieldName === 'servingQuantityColumn2')) {
      return false;
    }
    
    // For regular specs, check if dual column conditional fields should be mandatory
    if (fieldName === 'column1Heading' || fieldName === 'column2Heading' || fieldName === 'amountPreparedText' || fieldName === 'servingQuantityColumn2') {
      // These fields are mandatory only when dual column is selected
      return isDualColumnFormat(formToValidate?.factsPanelFormat || '');
    }

    if (fieldName === "columnsToDeclare") {
      if (isNutritionFactsNotRequiredFormat(formToValidate?.factsPanelFormat || "")) {
        return false;
      }
      const columnsPermission = (nutritionFormPermissions as any)?.columnsToDeclare;
      return columnsPermission?.isMandatory || false;
    }

    if (fieldName === "pdcaas") {
      const pdcaasPermission = (nutritionFormPermissions as any)?.pdcaas;
      return (
        isProteinClaimDvCalculationBasis(formToValidate?.dvCalculationBasis || "") &&
        (pdcaasPermission?.isMandatory ?? false)
      );
    }
    
    // For all other fields, check the mandatory setting from permissions
    const fieldPermission = (nutritionFormPermissions as any)?.[fieldName];
    if (fieldPermission) {
      return fieldPermission.isMandatory || false;
    }
    
    // Default to false if field not found in permissions
    return false;
  }, [nutritionForm?.factsPanelFormat, isPetFood, nutritionFormPermissions]);

  // Memoize mandatory fields
  const mandatoryFields = useMemo(() => {
    return getMandatoryFields(nutritionFormPermissions);
  }, [nutritionFormPermissions]);

  const normalizeNutrientName = useCallback((name?: string | null) => {
    return normalizeNutrientNameShared(name);
  }, []);

  const validatePetFoodDefaultNutrients = useCallback((formData: INutritionForm) => {
    const nutrients = Array.isArray(formData?.nutrients) ? formData.nutrients : [];
    const errors: string[] = [];
    const invalidNutrientNames = getInvalidNutrientNames(nutrients, { isPetFood: true });

    if (nutrients.length === 0) {
      errors.push("nutrients");
      return { isValid: false, hasNutrientErrors: true, errors, invalidNutrientNames: [] };
    }

    // Pet Food: every nutrient row must have Value to Appear on Label (valueToAppearOnLabel)
    if (invalidNutrientNames.length > 0) {
      errors.push("nutrients");
      return { isValid: false, hasNutrientErrors: true, errors, invalidNutrientNames };
    }

    const findByName = (target: string) =>
      nutrients.find((n) => normalizeNutrientName(n?.nutrient) === normalizeNutrientName(target));

    const missingOrEmptyDefaults = PET_FOOD_DEFAULT_NUTRIENTS.filter((d) => {
      const found = findByName(d.nutrient);
      if (!found) return true;
      // valueToAppearOnLabel already enforced above for all nutrient rows; still ensure defaults exist.
      return false;
    });

    if (missingOrEmptyDefaults.length > 0) {
      errors.push("nutrients");
      return { isValid: false, hasNutrientErrors: true, errors, invalidNutrientNames };
    }

    return { isValid: true, hasNutrientErrors: false, errors, invalidNutrientNames: [] };
  }, [normalizeNutrientName]);

  // Note: nutritionFormErrors state is now managed in FormContext

  /**
   * Helper function to validate a single nutrition form
   * This is extracted to be reusable for validating individual forms or all forms
   */
  const validateSingleForm = useCallback((formData: INutritionForm, factsPanelFormat?: string): { isValid: boolean; errors: string[] } => {
    let isFormValid = true;
    const newErrors: string[] = [];

    // Use provided factsPanelFormat or fall back to form data
    const formatToCheck = factsPanelFormat || formData?.factsPanelFormat || '';

    // Validate each mandatory field
    mandatoryFields.forEach((field) => {
      // Check if field is conditionally mandatory
      let isConditionallyMandatory = false;

      // For Pet Food, dual column fields are never mandatory (they're hidden)
      if (isPetFood && (field === 'column1Heading' || field === 'column2Heading' || field === 'amountPreparedText' || field === 'servingQuantityColumn2')) {
        // Skip - not mandatory for pet food
      } else if (field === 'column1Heading' || field === 'column2Heading' || field === 'amountPreparedText' || field === 'servingQuantityColumn2') {
        // These fields are mandatory only when dual column is selected
        isConditionallyMandatory = isDualColumnFormat(formatToCheck);
      } else if (field === "columnsToDeclare") {
        const fieldPermission = (nutritionFormPermissions as any)?.columnsToDeclare;
        const permMandatory = fieldPermission?.isMandatory ?? false;
        isConditionallyMandatory =
          permMandatory && !isNutritionFactsNotRequiredFormat(formatToCheck);
      } else if (field === "pdcaas") {
        const fieldPermission = (nutritionFormPermissions as any)?.pdcaas;
        isConditionallyMandatory =
          isProteinClaimDvCalculationBasis(formData?.dvCalculationBasis || "") &&
          (fieldPermission?.isMandatory ?? false);
      } else {
        // For all other fields, check the mandatory setting from permissions
        const fieldPermission = (nutritionFormPermissions as any)?.[field];
        if (fieldPermission) {
          isConditionallyMandatory = fieldPermission.isMandatory || false;
        }
      }

      if (!isConditionallyMandatory) {
        return; // Skip validation for fields that are not conditionally mandatory
      }

      // Composite field: validate value + unit separately but keep same error key
      if (field === "servingQuantityForCalculation") {
        if (!isCompositeNumberAndUnitValid((formData as any)?.servingQuantityForCalculation)) {
          newErrors.push(field);
          isFormValid = false;
        }
        return;
      }

      const value = formData[field as keyof typeof formData];
      const isEmpty =
        value === undefined ||
        value === null ||
        (typeof value === "string" && value.trim() === "");

      if (isEmpty) {
        newErrors.push(field);
        isFormValid = false;
      }
    });

    if (
      isProteinClaimDvCalculationBasis(formData?.dvCalculationBasis || "") &&
      String(formData?.pdcaas ?? "").trim() !== "" &&
      !isPdcaasInValidRange(formData?.pdcaas)
    ) {
      newErrors.push("pdcaas");
      isFormValid = false;
    }

    return { isValid: isFormValid, errors: newErrors };
  }, [mandatoryFields, isPetFood, nutritionFormPermissions]);

  const validateNutritionForm = useCallback((): boolean => {
    if (!nutritionForm) {
      showToast({
        type: "error",
        message: errorTexts?.FORM_DATA_NOT_AVAILABLE_ERROR || "Form data is not available"
      });
      return false;
    }

    // When user indicates Nutrition info is NOT required, bypass validation entirely
    // so Save/Next workflow can proceed while the form is disabled.
    if (isNutritionInfoNotRequired) {
      setNutritionFormErrors([]);
      return true;
    }

    const validationResult = validateSingleForm(nutritionForm);
    let { isValid: isFormValid, errors: newErrors } = validationResult;

    // Pet Food: default guaranteed analysis nutrients are mandatory (must exist + have guaranteedPercent)
    if (isPetFood) {
      const petFoodNutrientsValidation = validatePetFoodDefaultNutrients(nutritionForm);
      if (!petFoodNutrientsValidation.isValid) {
        newErrors = [...newErrors, ...petFoodNutrientsValidation.errors];
        isFormValid = false;
      }
    }

    // Update error state
    setNutritionFormErrors(newErrors);
    
    return isFormValid;
  }, [nutritionForm, validateSingleForm, setNutritionFormErrors, errorTexts, isPetFood, validatePetFoodDefaultNutrients, isNutritionInfoNotRequired]);

  const clearValidationErrors = useCallback(() => {
    setNutritionFormErrors([]);
  }, []);

  const hasFieldError = useCallback((fieldName: string): boolean => {
    // Only show errors if validation has been attempted
    // This prevents showing errors when creating new sections or switching sections
    // Before the onChange update, this wasn't needed because errors weren't persisting
    // But now we need this check to prevent stale errors from showing
    if (!nutritionValidationErrors) {
      return false;
    }
    return nutritionFormErrors.includes(fieldName);
  }, [nutritionFormErrors, nutritionValidationErrors]);

  const clearFieldError = useCallback((fieldName: string) => {
    setNutritionFormErrors(prev => prev.filter(field => field !== fieldName));
  }, []);

  // Keep the red highlight until the field is valid: whenever the form changes, drop the errors of fields
  // that now hold a valid value. Fields no longer clear their error on focus, so this is what clears it.
  useEffect(() => {
    setNutritionFormErrors(prev => {
      if (prev.length === 0) return prev;
      const kept = prev.filter(fieldName => isNutritionFieldFixed(fieldName, nutritionForm) !== true);
      return kept.length === prev.length ? prev : kept;
    });
  }, [nutritionForm, setNutritionFormErrors]);

  const setFieldError = useCallback((fieldName: string) => {
    setNutritionFormErrors(prev => {
      if (!prev.includes(fieldName)) {
        return [...prev, fieldName];
      }
      return prev;
    });
  }, []);

  /**
   * Comprehensive validation for NFP label generation
   * This is used both in NutritionDeclaration and workflow save/next
   * Returns whether the form is valid for generating NFP labels
   * Also sets field errors for UI highlighting
   */
  const validateNutritionDataForDeclaration = useCallback((): NutritionDeclarationValidationResult => {
    // Use ref to get latest form data to avoid stale closure issues
    // Also use nutritionForm directly as fallback to ensure we have latest data
    const formToValidate = nutritionFormRef?.current || nutritionForm;

    // When Nutrition info is not required, treat as valid for declaration/workflow purposes.
    if (isProduceRacTjLabel && String((formToValidate as any)?.nutritionInfoRequired ?? "Yes").trim().toLowerCase() === "no") {
      setNutritionFormErrors([]);
      return { isValid: true, hasNutrientErrors: false, invalidNutrientNames: [] };
    }
    
    const fieldErrors: string[] = [];
    
    // ===== PET FOOD VALIDATION (Use permissions-based validation) =====
    if (isPetFood) {
      // For pet food, use the same permissions-based validation as validateNutritionForm
      // This ensures we only validate fields that are actually mandatory according to PetFoodNutrition.json
      let isFormValid = true;
      
      mandatoryFields.forEach((field) => {
        // Check if field is conditionally mandatory
        const isConditionallyMandatory = isFieldConditionallyMandatory(field);
        
        if (!isConditionallyMandatory) {
          return; // Skip validation for fields that are not conditionally mandatory
        }

        // Composite field: validate value + unit separately but keep same error key
        if (field === "servingQuantityForCalculation") {
          if (!isCompositeNumberAndUnitValid((formToValidate as any)?.servingQuantityForCalculation)) {
            fieldErrors.push(field);
            isFormValid = false;
          }
          return;
        }
        
        const value = formToValidate?.[field as keyof typeof formToValidate];
        const isEmpty = 
          value === undefined ||
          value === null ||
          (typeof value === "string" && value.trim() === "");

        if (isEmpty) {
          fieldErrors.push(field);
          isFormValid = false;
        }
      });

      // Pet Food: default guaranteed analysis nutrients are mandatory (must exist + have guaranteedPercent)
      const petFoodNutrientsValidation = validatePetFoodDefaultNutrients(formToValidate as INutritionForm);
      if (!petFoodNutrientsValidation.isValid) {
        fieldErrors.push(...petFoodNutrientsValidation.errors);
        isFormValid = false;
      }

      setNutritionFormErrors(fieldErrors);
      return {
        isValid: isFormValid,
        hasNutrientErrors: !petFoodNutrientsValidation.isValid,
        invalidNutrientNames: petFoodNutrientsValidation.invalidNutrientNames || [],
      };
    }
    
    // ===== STANDARD NUTRITION VALIDATION (Food & Beverages) =====
    const isDualColumn = isDualColumnFormat(formToValidate?.factsPanelFormat || '');
    
    // Check essential form fields
    if (!formToValidate?.nutritionDataSubmissionMethod?.trim()) {
      fieldErrors.push('nutritionDataSubmissionMethod');
    }
    if (!formToValidate?.factsPanelFormat?.trim()) {
      fieldErrors.push('factsPanelFormat');
    }
    if (!formToValidate?.servingSizeAndHouseholdMeasure?.trim()) {
      fieldErrors.push('servingSizeAndHouseholdMeasure');
    }
    if (!formToValidate?.servingsPerContainer?.trim()) {
      fieldErrors.push('servingsPerContainer');
    }
    if (!isCompositeNumberAndUnitValid((formToValidate as any)?.servingQuantityForCalculation)) {
      fieldErrors.push('servingQuantityForCalculation');
    }
    
    // Check additional required core fields
    if (!formToValidate?.headingText?.trim()) {
      fieldErrors.push('headingText');
    }
    if (
      !isNutritionFactsNotRequiredFormat(formToValidate?.factsPanelFormat || "") &&
      !formToValidate?.columnsToDeclare?.trim()
    ) {
      fieldErrors.push('columnsToDeclare');
    }
    if (!formToValidate?.dvCalculationBasis?.trim()) {
      fieldErrors.push('dvCalculationBasis');
    }
    if (
      isProteinClaimDvCalculationBasis(formToValidate?.dvCalculationBasis || "") &&
      String(formToValidate?.pdcaas ?? "").trim() !== "" &&
      !isPdcaasInValidRange(formToValidate?.pdcaas)
    ) {
      fieldErrors.push('pdcaas');
    }
    if (!formToValidate?.rdiCalculationBasis?.trim()) {
      fieldErrors.push('rdiCalculationBasis');
    }
    if (!formToValidate?.calculateAmounts?.trim()) {
      fieldErrors.push('calculateAmounts');
    }
    if (!formToValidate?.dataSource?.trim()) {
      fieldErrors.push('dataSource');
    }
    
    // Check dual column conditional fields
    if (isDualColumn) {
      if (!formToValidate?.column1Heading?.trim()) {
        fieldErrors.push('column1Heading');
      }
      if (!formToValidate?.column2Heading?.trim()) {
        fieldErrors.push('column2Heading');
      }
      if (!formToValidate?.amountPreparedText?.trim()) {
        fieldErrors.push('amountPreparedText');
      }
      if (!formToValidate?.servingQuantityColumn2?.trim()) {
        fieldErrors.push('servingQuantityColumn2');
      }
    }
    
    // Check if nutrients are present and collect all incomplete rows
    const nutrients = formToValidate?.nutrients || [];
    const invalidNutrientNames = getInvalidNutrientNames(nutrients, {
      isDualColumn,
      isPetFood: false,
    });
    const hasNutrientErrors = nutrients.length === 0 || invalidNutrientNames.length > 0;

    if (nutrients.length === 0) {
      setNutritionFormErrors(fieldErrors);
      return { isValid: false, hasNutrientErrors: true, invalidNutrientNames: [] };
    }

    setNutritionFormErrors(fieldErrors);

    const isValid = fieldErrors.length === 0 && !hasNutrientErrors;

    return { isValid, hasNutrientErrors, invalidNutrientNames };
  }, [
    nutritionForm,
    isPetFood,
    setNutritionFormErrors,
    isProduceRacTjLabel,
    mandatoryFields,
    isFieldConditionallyMandatory,
    validatePetFoodDefaultNutrients,
  ]);

  /**
   * Validate all nutrition forms across all sections
   * Uses nutritionSections from FormContext to get all sections
   * For the current active section, uses nutritionForm (with user's current changes)
   * For all other sections, uses API data from nutritionSections
   * Returns validation results for each section
   */
  const validateAllNutritionForms = useCallback((): AllFormsValidationResult => {
    const results: SectionValidationResult[] = [];
    
    // Get all sections from nutritionSections
    const sections = nutritionSections?.nutritionTab || [];
    
    if (sections.length === 0) {
      return {
        allValid: true,
        results: [],
        invalidSections: []
      };
    }

    // Get current form ID to identify which section is currently active
    const currentFormId = nutritionForm?.id;

    // Validate each section
    sections.forEach((section: NutritionTabItem) => {
      try {
        let formData: INutritionForm;
        let factsPanelFormat: string;
        let sectionName: string;

        // Check if this is the current active section (has unsaved changes)
        const isCurrentSection = currentFormId && String(currentFormId) === String(section.id);
        
        if (isCurrentSection && nutritionForm) {
          // Use current form data (includes user's unsaved changes)
          formData = nutritionForm;
          factsPanelFormat = nutritionForm.factsPanelFormat || '';
          sectionName = nutritionForm.sectionName || section.sectionName || `Section ${section.id}`;
          
          if (process.env.NODE_ENV === 'development') {
            console.log(` [validateAllNutritionForms] Using current form data for section ${section.id}`);
          }
        } else {
          // Use API data for other sections
          formData = transformSectionToNutritionForm(section);
          factsPanelFormat = section.factsPanelFormatForLabel || '';
          sectionName = section.sectionName || `Section ${section.id}`;
        }
        
        // When Nutrition info is not required, mark section valid (skip mandatory checks)
        const notRequired = isProduceRacTjLabel && String((formData as any)?.nutritionInfoRequired ?? "Yes").trim().toLowerCase() === "no";
        if (notRequired) {
          results.push({
            sectionId: section.id,
            sectionName,
            isValid: true,
            errors: []
          });
          return;
        }

        // Validate the form using the same logic as validateNutritionForm
        const validationResult = validateSingleForm(formData, factsPanelFormat);
        
        results.push({
          sectionId: section.id,
          sectionName: sectionName,
          isValid: validationResult.isValid,
          errors: validationResult.errors
        });
      } catch (error) {
        console.error(` [validateAllNutritionForms] Error validating section ${section.id}:`, error);
        // If transformation fails, mark as invalid
        results.push({
          sectionId: section.id,
          sectionName: section.sectionName || `Section ${section.id}`,
          isValid: false,
          errors: ['transformation_error']
        });
      }
    });

    // Filter invalid sections
    const invalidSections = results.filter(result => !result.isValid);
    
    // Check if all sections are valid
    const allValid = invalidSections.length === 0;

    if (process.env.NODE_ENV === 'development') {
      console.log(' [validateAllNutritionForms] Validation results:', {
        totalSections: results.length,
        validSections: results.filter(r => r.isValid).length,
        invalidSections: invalidSections.length,
        results
      });
    }

    return {
      allValid,
      results,
      invalidSections
    };
  }, [nutritionSections, nutritionForm, validateSingleForm, isProduceRacTjLabel]);

  return {
    validateNutritionForm,
    validateNutritionDataForDeclaration,
    validateAllNutritionForms,
    nutritionFormErrors,
    setNutritionFormErrors,
    clearValidationErrors,
    hasFieldError,
    clearFieldError,
    setFieldError,
    isFieldConditionallyMandatory
  };
};
